import { useCallback, useRef } from 'react';

/**
 * Hook that wraps a function to only execute once
 * @param fn The function to execute once
 * @returns A memoized callback that will only execute the function once
 */
export const useTriggerOnce = <T extends (...args: never[]) => void>(fn: T): T => {
  const hasTrackedRef = useRef(false);
  const fnRef = useRef(fn);

  // Always keep the latest function reference
  fnRef.current = fn;

  const trackOnce = useCallback(
    (...args: Parameters<T>) => {
      if (!hasTrackedRef.current) {
        hasTrackedRef.current = true;
        fnRef.current(...args);
      }
    },
    [], // No dependencies - we use refs to always get latest values
  ) as T;

  return trackOnce;
};
